import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ScheduleCfpSessionPage.module.css';

function CarouselNavButton({ direction, onClick, disabled, label }) {
  return (
    <button
      type="button"
      className={`${styles.topicCarouselNav} ${
        direction === 'prev' ? styles.topicCarouselNavPrev : styles.topicCarouselNavNext
      } ${disabled ? styles.topicCarouselNavDisabled : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d={direction === 'prev' ? 'M14 6L8 12L14 18' : 'M10 6L16 12L10 18'}
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

const DRAG_START_THRESHOLD = 8;
const SWIPE_AXIS_RATIO = 1.25;

function isInteractiveTarget(target) {
  return target instanceof Element && target.closest('button, a, input, textarea, select, [data-topic-toggle]');
}

function CfpTopicCarousel({ children, segmentId }) {
  const trackRef = useRef(null);
  const pointerStartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragOccurredRef = useRef(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const snapToNearestSlide = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const slides = track.querySelectorAll(`.${styles.topicCarouselSlide}`);
    if (!slides.length) return;

    const trackLeft = track.getBoundingClientRect().left;
    let closestSlide = slides[0];
    let closestDistance = Infinity;

    slides.forEach((slide) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = slide;
      }
    });

    track.scrollTo({
      left: closestSlide.offsetLeft,
      behavior: 'smooth',
    });
  }, []);

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const { scrollLeft, scrollWidth, clientWidth } = track;
    const maxScroll = scrollWidth - clientWidth;
    setCanScrollPrev(scrollLeft > 4);
    setCanScrollNext(scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    updateScrollState();

    track.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      track.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [segmentId, updateScrollState]);

  useEffect(() => {
    const track = trackRef.current;
    if (track) {
      track.scrollTo({ left: 0 });
      updateScrollState();
    }
  }, [segmentId, updateScrollState]);

  const scrollByPage = useCallback(
    (direction) => {
      const track = trackRef.current;
      if (!track) return;

      const slides = Array.from(track.querySelectorAll(`.${styles.topicCarouselSlide}`));
      if (!slides.length) return;

      const trackLeft = track.getBoundingClientRect().left;
      const currentIndex = slides.reduce((bestIndex, slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        const bestDistance = Math.abs(slides[bestIndex].getBoundingClientRect().left - trackLeft);
        return distance < bestDistance ? index : bestIndex;
      }, 0);

      const targetIndex = Math.min(Math.max(currentIndex + direction, 0), slides.length - 1);
      track.scrollTo({
        left: slides[targetIndex].offsetLeft,
        behavior: 'smooth',
      });
    },
    [],
  );

  const handlePointerDown = useCallback((event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (isInteractiveTarget(event.target)) return;

    const track = trackRef.current;
    if (!track) return;

    pointerStartRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      scrollLeft: track.scrollLeft,
    };
    isDraggingRef.current = false;
  }, []);

  const handlePointerMove = useCallback((event) => {
    const start = pointerStartRef.current;
    const track = trackRef.current;
    if (!start || !track || start.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;

    if (!isDraggingRef.current) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      if (absX < DRAG_START_THRESHOLD || absX < absY * SWIPE_AXIS_RATIO) return;

      isDraggingRef.current = true;
      dragOccurredRef.current = true;
      setIsDragging(true);
      track.setPointerCapture(event.pointerId);
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
    }

    event.preventDefault();
    track.scrollLeft = start.scrollLeft - deltaX;
  }, []);

  const endPointerInteraction = useCallback(
    (event) => {
      const start = pointerStartRef.current;
      const track = trackRef.current;
      if (!start || !track || start.pointerId !== event.pointerId) return;

      if (track.hasPointerCapture(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }

      if (isDraggingRef.current) {
        snapToNearestSlide();
      }

      track.style.scrollBehavior = '';
      track.style.scrollSnapType = '';
      pointerStartRef.current = null;
      isDraggingRef.current = false;
      setIsDragging(false);

      if (dragOccurredRef.current) {
        window.setTimeout(() => {
          dragOccurredRef.current = false;
        }, 100);
      }
    },
    [snapToNearestSlide],
  );

  const handleClickCapture = useCallback((event) => {
    if (dragOccurredRef.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  return (
    <div className={styles.topicCarousel}>
      <div className={styles.topicCarouselViewport}>
        <div
          ref={trackRef}
          className={`${styles.topicCarouselTrack} ${isDragging ? styles.topicCarouselTrackDragging : ''}`}
          role="list"
          aria-label="Priority topic cards"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endPointerInteraction}
          onPointerCancel={endPointerInteraction}
          onClickCapture={handleClickCapture}
        >
          {children}
        </div>
      </div>

      <CarouselNavButton
        direction="prev"
        onClick={() => scrollByPage(-1)}
        disabled={!canScrollPrev}
        label="Show previous topics"
      />
      <CarouselNavButton
        direction="next"
        onClick={() => scrollByPage(1)}
        disabled={!canScrollNext}
        label="Show more topics"
      />
    </div>
  );
}

export default CfpTopicCarousel;
